import { useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'hooks/useTranslation'
import { formatCurrencyWithoutZeroCents } from 'utils/currency'
import { Tooltip } from 'components/Tooltip'
import { Icon } from 'components/Icon'
import heroImage from '../../../../../../../../public/assets/product-v2/freeze-dried-box/beef-chicken-fd-box-with-bowl.webp'
import {
  PurchaseHeroRoot,
  PurchaseHeroInner,
  ImageColumn,
  HeroImageWrapper,
  ContentColumn,
  Headline,
  Description,
  SelectorCard,
  SelectorGroup,
  SelectorLabel,
  QuantityLabelRow,
  ToggleRow,
  ToggleButton,
  SubscribeMessage,
  TooltipTrigger,
} from './PurchaseHero.styles'
import { Button } from 'components/Button'
import { ProductVariant } from 'models/Product'
import { AddToCart } from 'features/ECommerce/hooks/useAddToCart'
import { FreezeDriedBoxId } from 'models/Box'

type Bags = 2 | 4 | 6

type HeroProps = {
  products: ProductVariant[]
  onAdd: AddToCart
}

const calculateQuantity = (bags: Bags) => bags / 2

export const PurchaseHero = ({ products, onAdd }: HeroProps) => {
  const { t } = useTranslation()
  const [selectedProduct, setSelectedProduct] = useState<ProductVariant>(
    () => products.find((product) => product.slug === FreezeDriedBoxId.CHICKEN) ?? products[0]
  )
  const [selectedBags, setSelectedBags] = useState<Bags>(2)

  const productOptions: { productId: FreezeDriedBoxId; label: string }[] = [
    {
      productId: FreezeDriedBoxId.CHICKEN,
      label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.recipe-chicken'),
    },
    {
      productId: FreezeDriedBoxId.BEEF,
      label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.recipe-beef'),
    },
    {
      productId: FreezeDriedBoxId.MIXED,
      label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.recipe-combo'),
    },
  ]

  const bagOptions: { value: Bags; label: string }[] = [
    { value: 2, label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.bags-2') },
    { value: 4, label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.bags-4') },
    { value: 6, label: t('products-v2:freeze-dried-raw-nuggets.purchase-hero.bags-6') },
  ]

  const handleProductSelect = (productId: FreezeDriedBoxId) => {
    const product = products.find((product) => product.slug === productId)
    if (product) {
      setSelectedProduct(product)
    }
  }

  const cartPrice = formatCurrencyWithoutZeroCents(
    selectedProduct.price.subtotal * calculateQuantity(selectedBags)
  )

  return (
    <PurchaseHeroRoot id="hero">
      <PurchaseHeroInner>
        <ImageColumn>
          <HeroImageWrapper>
            <Image
              src={heroImage}
              alt={t('products-v2:freeze-dried-raw-nuggets.purchase-hero.hero-image-alt')}
              layout="fill"
              objectFit="cover"
              priority
            />
          </HeroImageWrapper>
        </ImageColumn>
        <ContentColumn>
          <Headline>{t('products-v2:freeze-dried-raw-nuggets.purchase-hero.headline')}</Headline>
          <Description>
            {t('products-v2:freeze-dried-raw-nuggets.purchase-hero.description')}
          </Description>
          <SelectorCard>
            <SelectorGroup>
              <SelectorLabel>
                {t('products-v2:freeze-dried-raw-nuggets.purchase-hero.select-recipe-label')}
              </SelectorLabel>
              <ToggleRow>
                {productOptions.map(({ productId, label }) => (
                  <ToggleButton
                    key={productId}
                    active={productId === selectedProduct.slug}
                    aria-pressed={selectedProduct.slug === productId}
                    onClick={() => handleProductSelect(productId)}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleRow>
            </SelectorGroup>
            <SelectorGroup>
              <QuantityLabelRow>
                <SelectorLabel>
                  {t('products-v2:freeze-dried-raw-nuggets.purchase-hero.select-quantity-label')}
                </SelectorLabel>
                <Tooltip
                  text={t('products-v2:freeze-dried-raw-nuggets.purchase-hero.quantity-tooltip')}
                  instant
                >
                  <TooltipTrigger
                    aria-label={t(
                      'products-v2:freeze-dried-raw-nuggets.purchase-hero.quantity-tooltip-aria-label'
                    )}
                  >
                    <Icon name="information-circle-line" size="3" color="accent-black" />
                  </TooltipTrigger>
                </Tooltip>
              </QuantityLabelRow>
              <ToggleRow>
                {bagOptions.map(({ value, label }) => (
                  <ToggleButton
                    key={value}
                    active={selectedBags === value}
                    aria-pressed={selectedBags === value}
                    onClick={() => setSelectedBags(value)}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleRow>
            </SelectorGroup>
            <Button
              className='cta'
              onClick={() =>
                selectedProduct &&
                onAdd(
                  selectedProduct.productVariantId,
                  selectedProduct.productId,
                  calculateQuantity(selectedBags),
                  selectedProduct.name
                )
              }
            >
              {`${t('products-v2:freeze-dried-raw-nuggets.purchase-hero.add-to-cart')} - ${cartPrice}`}
            </Button>
            <SubscribeMessage>
              {t('products-v2:freeze-dried-raw-nuggets.purchase-hero.subscribe-message')}
            </SubscribeMessage>
          </SelectorCard>
        </ContentColumn>
      </PurchaseHeroInner>
    </PurchaseHeroRoot>
  )
}

PurchaseHero.displayName = 'PurchaseHero'
